import React, {useCallback, useContext, useEffect, useState} from "react";
import {Alert, Button, Input, message, Modal, Space, Table} from "antd";
import {InterestServiceContext} from "../../Contexts";
import {INTEREST_MEMORY_EXPLANATION, InterestMemory, InterestMemoryRule} from "../../domain/InterestMemory";
import * as styles from "./InterestMemoryPanel.module.css";

interface Props {
    interestId: string;
    canManage: boolean;
}

export default function InterestMemoryPanel({interestId, canManage}: Props) {
    const service = useContext(InterestServiceContext);
    const [memory, setMemory] = useState<InterestMemory>();
    const [editing, setEditing] = useState<InterestMemoryRule>();
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();
    const reload = useCallback(() => service.getMemory(interestId).then(setMemory), [service, interestId]);

    useEffect(() => {
        let active = true;

        setMemory(undefined);
        setEditing(undefined);
        setError(undefined);

        service.getMemory(interestId).then(value => {
            if (active) {
                setMemory(value);
            }
        }).catch(() => {
            if (active) {
                setError("Unable to load interest memory.");
            }
        });

        return () => { active = false; };
    }, [service, interestId]);

    async function change(rule: InterestMemoryRule, action: "edit" | "delete") {
        if (!memory) {
            return;
        }

        setBusy(true);
        setError(undefined);

        try {
            if (action === "edit") {
                await service.updateMemoryRule(interestId, rule.id, text);
            } else {
                await service.deleteMemoryRule(interestId, rule.id);
            }

            setEditing(undefined);
            message.success("Interest memory updated. It will apply at the next item evaluation.");

            await reload();
        } catch (error: any) {
            setError(error?.response?.data?.message ?? "Unable to update interest memory.");
        } finally {
            setBusy(false);
        }
    }

    return <section className={styles.panel}>
        <h2>Interest memory</h2>
        <p>{INTEREST_MEMORY_EXPLANATION}</p>
        {error && <Alert type="error" showIcon title={error}
                         action={<Button onClick={() => reload().then(() => setError(undefined)).catch(() => setError("Unable to load interest memory."))}>Reload memory</Button>}/>}
        <Table<InterestMemoryRule> rowKey="id" size="small" pagination={false} loading={!memory && !error}
                                   dataSource={memory?.rules ?? []}
                                   locale={{emptyText: "No memory rules. Add feedback using Not interested in a report."}}
                                   columns={[
                                       {title: "Rule", dataIndex: "text"},
                                       ...(canManage ? [{
                                           title: "Actions",
                                           key: "actions",
                                           render: (_: unknown, rule: InterestMemoryRule) => <Space>
                                               <Button disabled={busy} onClick={() => {
                                                   setEditing(rule);
                                                   setText(rule.text);
                                                   setError(undefined);
                                               }}>Edit</Button>
                                               <Button danger disabled={busy} onClick={() => Modal.confirm({
                                                   title: "Delete memory rule?",
                                                   content: INTEREST_MEMORY_EXPLANATION,
                                                   okText: "Delete",
                                                   okButtonProps: {danger: true},
                                                   onOk: () => change(rule, "delete")
                                               })}>Delete</Button>
                                           </Space>
                                       }] : [])
                                   ]}/>
        <Modal open={!!editing} title="Edit interest memory" okText="Save memory"
               confirmLoading={busy} okButtonProps={{disabled: !text.trim()}} closable={!busy} keyboard={!busy}
               cancelButtonProps={{disabled: busy}} mask={{closable: false}}
               onCancel={() => setEditing(undefined)} onOk={() => editing && change(editing, "edit")}>
            <p>{INTEREST_MEMORY_EXPLANATION}</p>
            {error && <Alert type="error" showIcon title={error}/>}
            <Input.TextArea disabled={busy} value={text} onChange={event => setText(event.target.value)} maxLength={1000} rows={4}/>
        </Modal>
    </section>;
}
